import type { ApiResource } from './types';

/**
 * Kinds the console learned about from the cluster rather than from its own
 * catalogue. This is what makes a freshly installed operator's CRDs — or an
 * ASO type, or a kro composition — browsable without a code change.
 *
 * Kept free of React so it can be reasoned about (and tested) on its own.
 */

/** Shape of a discovered kind id: `plural` or `plural.group.example.com`. */
export const DISCOVERED_KIND_ID_PATTERN =
  /^[a-z0-9-]+(\.[a-z0-9]([a-z0-9.-]*[a-z0-9])?)?$/;

/**
 * Stable id for a discovered kind, used in the nav and the `section` URL
 * parameter. Bare plural for the core group, `plural.group` otherwise — the
 * way kubectl addresses them, and never a collision with a curated id.
 */
export function discoveredKindId(plural: string, group: string): string {
  return group ? `${plural}.${group}` : plural;
}

export interface DiscoveredKind<C> {
  id: string;
  label: string;
  kind: string;
  plural: string;
  apiBase: string;
  group: string;
  namespaced: boolean;
  columns: C[];
  discovered: true;
}

/**
 * Turns a cluster's resource catalogue into kinds the console can open.
 *
 * Curated definitions win: they carry tuned columns and keep their short ids,
 * so existing links and the sidebar are unaffected. Discovery only fills in
 * what nobody wrote a page for. Matching is on group *and* plural, so two
 * groups that both serve `routes` stay distinct.
 */
export function buildDiscoveredKinds<C>(
  resources: ApiResource[],
  curated: { group: string; plural: string }[],
  columns: C[],
): DiscoveredKind<C>[] {
  const covered = new Set(curated.map(kind => `${kind.group}/${kind.plural}`));
  return resources
    .filter(resource => !covered.has(`${resource.group}/${resource.plural}`))
    .map(resource => ({
      id: discoveredKindId(resource.plural, resource.group),
      label: resource.kind,
      kind: resource.kind,
      plural: resource.plural,
      apiBase: resource.group
        ? `/apis/${resource.group}/${resource.version}`
        : '/api/v1',
      group: resource.group,
      namespaced: resource.namespaced,
      columns,
      discovered: true as const,
    }));
}
